import gql from 'graphql-tag';
import React from 'react';
import {
  CheckoutDisplayFields,
} from '../schemas/rql';
import { CheckoutStep, PAYMENT_STEP, SHIPPING_STEP } from '../lib/checkout_step';

type Checkout = CheckoutDisplayFields.Fragment;

export type AuthVariant = 'sign-in' | 'sign-up';

interface AuthModalState {
  isOpen: boolean;
  variant: AuthVariant;
  open: (variant: AuthVariant) => void;
  close: () => void;
}

export interface CheckoutDisplay {
  step: CheckoutStep;
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  layoutQueryName: string;
  authModal: AuthModalState;
}

export const CheckoutDisplayContext = React.createContext<CheckoutDisplay>({
  step: SHIPPING_STEP,
  isLoading: false,
  setLoading: (_) => { },
  layoutQueryName: null,
  authModal: {
    isOpen: false,
    variant: null,
    open: () => { },
    close: () => { },
  },
});

/**
 * Use to access the checkout layout's state
 */
export function useCheckoutDisplay() {
  return React.useContext(CheckoutDisplayContext);
}

/**
 * Use to hydrate a layout with a loading state.
 *
  * Only necessary once per top-level checkout layout
 */
export function useCheckoutDisplayState(checkout: Checkout, layoutQueryName: string): CheckoutDisplay {
  const step = findCheckoutStep(checkout);
  const [isLoading, setLoading] = React.useState(false);
  const [authVariant, setAuthModalVariant] = React.useState<AuthVariant>();

  function openModal(variant: AuthVariant) {
    setAuthModalVariant(variant);
  }

  function closeModal() {
    setAuthModalVariant(null);
  }

  return {
    step,
    isLoading,
    setLoading,
    layoutQueryName,
    authModal: {
      isOpen: !!authVariant,
      variant: authVariant,
      open: openModal,
      close: closeModal,
    },
  };
}

export function findCheckoutStep(checkout: Checkout): CheckoutStep {
  if (!checkout?.shippingAddress?.isComplete) {
    return SHIPPING_STEP;
  }

  return PAYMENT_STEP;
}

export const CheckoutDisplayFragment = gql`
  fragment CheckoutDisplayFields on Checkout {
    _id
    shippingAddress {
      isComplete
    }
    paymentMethod {
      isComplete
    }
  }
`;
